import React, { useState, useContext, useCallback, useEffect } from 'react';
import useScrollLock from 'js/utils/hooks/useScrollLock';
import { ImpressionPropsGenerator } from 'js/social/ImpressionHelper';
import { Paper, OpenalexWork } from '../../shared/types';
import useNewsInteractions from '../../News/useNewsInteractions';
import { ReaderWithLoadedSidebar } from './ReaderWithLoadedSidebar';
import ReaderButtonRow from './ReaderButtonRow';
import AttachmentAutoDownloader from 'js/social/shared/AttachmentAutoDownloader';
import { NewsType } from 'js/social/shared/types';

type ReaderProps = {
  newsType: NewsType;
  newsId: string | number;
  work: Paper | OpenalexWork;
  impressionPropsGen: ImpressionPropsGenerator;
};

type ReaderContextValue = {
  showReader(readerData: ReaderProps): void;
};

const ReaderContext = React.createContext<ReaderContextValue>({
  showReader: (_: ReaderProps) => {},
});

type ProviderProps = {
  children: React.ReactNode;
  onDownload?: () => void;
  updateWorkBookmarkStatus: (workId: number, bookmarked: boolean) => void;
  showRelatedPapersSidebar: boolean;
};

export const useReader = () => useContext(ReaderContext);

export function ReaderProvider({
  children,
  onDownload,
  updateWorkBookmarkStatus,
  showRelatedPapersSidebar,
}: ProviderProps) {
  const [download, setDownload] = useState(false);
  const [readerData, setReaderData] = useState<ReaderProps | null>(null);

  const { actions, permissions } = useNewsInteractions({
    newsId: readerData?.work?.id ?? 0,
    newsType: 'work',
    paperType: null,
    work: readerData?.work,
    tags: [],
    updateWorkBookmarkStatus,
    setUserFollowing: () => {},
    removeUserContent: () => {},
    addFeedNews: () => {},
    addFeedComment: () => {},
    removeFeedNews: () => {},
    updateFeedNews: () => {},
    likeFeedNews: () => {},
    unlikeFeedNews: () => {},
    repostFeedNews: () => {},
    unrepostFeedNews: () => {},
    messageText: {
      bookmarkSuccess: 'Saved to library',
      bookmarkFailure: 'Failed to save to library',
      unbookmarkSuccess: 'Removed from library',
      unbookmarkFailure: 'Failed to remove from library',
    },
  });

  useScrollLock(!!readerData);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // If we have reader data and the state is null (back button pressed)
      if (readerData && !event.state?.reader) {
        setReaderData(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [readerData]);

  const showReader = useCallback(
    ({ newsType, newsId, work, impressionPropsGen }: ReaderProps) => {
      if (typeof work.id !== 'number') return; // skip unless work is Paper

      // Push new state to history when opening reader
      window.history.pushState({ reader: true }, '', window.location.href);

      setReaderData({
        newsType,
        newsId,
        work: work as Paper,
        impressionPropsGen,
      });
    },
    [],
  );

  const handleDownload = useCallback(async () => {
    if (!readerData) return;

    setDownload(true);
    onDownload && (await onDownload());
  }, [onDownload, readerData]);

  return (
    <ReaderContext.Provider value={{ showReader }}>
      {children}
      {readerData && (
        <>
          <ReaderWithLoadedSidebar
            open={true}
            newsType={readerData.newsType}
            newsId={readerData.newsId}
            work={readerData.work as Paper}
            onDownload={handleDownload}
            impressionPropsGen={readerData.impressionPropsGen}
            onClose={() => setReaderData(null)}
            buttonRow={
              <ReaderButtonRow
                bookmarked={permissions.isBookmarked || false}
                onBookmark={actions.bookmarkWork}
                onUnbookmark={actions.unbookmarkWork}
              />
            }
            showRelatedPaperSidebar={showRelatedPapersSidebar}
          />
          {download && (
            <AttachmentAutoDownloader
              work={readerData.work as Paper}
              onComplete={() => setDownload(false)}
            />
          )}
        </>
      )}
    </ReaderContext.Provider>
  );
}
